import {
  ApiError,
  ApiResourceNotFoundError,
  isRetryableError,
} from '@shared/api/errors';
import {
  INACTIVE_RESERVATION_STATUSES,
  type ReservationStatus,
} from '@shared/types/reservations';
import { NOT_FOUND } from '@shared/statusCodes';
import { getReservation } from 'webReservations/reservationDetails/apiHelpers';

export const RESERVATION_NOT_CONFIRMED_ERROR_MESSAGE =
  'Your reservation is still being confirmed. Please wait a moment and check your reservations.';

export const RESERVATION_NO_LONGER_AVAILABLE_ERROR_MESSAGE =
  'This reservation is no longer available.';

const DEFAULT_MAX_ATTEMPTS = 10;
const DEFAULT_INTERVAL_MS = 300;

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

const isInactiveReservationStatus = (status: ReservationStatus): boolean =>
  INACTIVE_RESERVATION_STATUSES.includes(status);

interface WaitForConfirmedReservationOptions {
  getReservationFn?: typeof getReservation;
  intervalMs?: number;
  maxAttempts?: number;
}

export const waitForConfirmedReservation = async (
  reservationId: string,
  {
    getReservationFn = getReservation,
    intervalMs = DEFAULT_INTERVAL_MS,
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
  }: WaitForConfirmedReservationOptions = {},
): Promise<void> => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const reservation = await getReservationFn(reservationId);

      if (reservation.status === 'active') {
        return;
      }

      if (isInactiveReservationStatus(reservation.status)) {
        throw new Error(RESERVATION_NO_LONGER_AVAILABLE_ERROR_MESSAGE);
      }
    } catch (error) {
      const isNotFound =
        error instanceof ApiResourceNotFoundError ||
        (error instanceof ApiError && error.statusCode === NOT_FOUND);

      if (!isNotFound && !isRetryableError(error)) {
        throw error;
      }
    }

    if (attempt < maxAttempts - 1) {
      await sleep(intervalMs);
    }
  }

  throw new Error(RESERVATION_NOT_CONFIRMED_ERROR_MESSAGE);
};
